import { ICellRendererParams, ValueGetterFunc } from "@ag-grid-community/core";
import { useDisclosure } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { CapitalCallReallocationEditDateModal } from "features/capital-activity/event/reallocation/CapitalCallReallocationEditDateModal";
import { CapitalCallReallocationStatCard } from "features/capital-activity/event/reallocation/CapitalCallReallocationStatCard";
import {
  CapitalCallInvestmentSelectCellRenderer,
  categoryIdToClassification,
} from "features/capital-call/drawdown/CapitalCallDrawdownFields";
import { useGetAllocationsByLookupKey } from "features/drawdown/DrawdownUtils";
import {
  getFundFamilyEventCapitalCallReallocationTableRetrieveQueryKey,
  useCapitalCallReallocationCreate,
  useFundFamilyEventCapitalCallReallocationTableRetrieveSuspense,
} from "gen/endpoints/capital-call-reallocation/capital-call-reallocation";
import { useCapitalCallReallocationDraftDataRetrieveSuspense } from "gen/endpoints/capital-call-reallocation-draft-data/capital-call-reallocation-draft-data";
import {
  getFundFamilyEventRetrieveQueryKey,
  useFundFamilyEventRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { useTransactionCodeConfigListSuspense } from "gen/endpoints/transactions/transactions";
import { CallOrDistributionEnum } from "gen/model/callOrDistributionEnum";
import { CapitalCallReallocationCreateDataRequest } from "gen/model/capitalCallReallocationCreateDataRequest";
import { FundFamilyUseAllocationTableColumnDataCapitalCallReallocation } from "gen/model/fundFamilyUseAllocationTableColumnDataCapitalCallReallocation";
import { FundFamilyUseAllocationTableData } from "gen/model/fundFamilyUseAllocationTableData";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { capitalCallReallocationCreateBody } from "gen/schemas/capital-call-reallocation/capital-call-reallocation";
import { cloneDeep, get } from "lodash-es";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "shared/components/form/Form";
import { FundFamilyUseAllocationTable } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTable";
import { fundFamilyUseAllocationTableResolver } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableFormUtils";
import {
  FundFamilyUseAllocationTableColumnCellRendererSelector,
  FundFamilyUseAllocationTableContext,
  FundFamilyUseAllocationTableGetAllocationFn,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import {
  PopulateEmptyFundFamilyUseAllocationTableRowFn,
  useFundFamilyUseAllocationTableFormRowController,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableRowController";
import { useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { SimpleSelect } from "shared/components/input/select/SimpleSelect";
import PageBody from "shared/components/layout/PageBody";
import { PageHeader } from "shared/components/layout/PageHeader";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { getMaybernTableCellError } from "shared/components/table/maybern/MaybernTableErrors";
import { MaybernTableFocusCellRendererWrapper } from "shared/components/table/maybern/MaybernTableFocusCellRendererWrapper";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { nowAsIsoDate } from "shared/utils/DateUtils";
import { decimalOrZero } from "shared/utils/DecimalUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import invariant from "shared/utils/TinyInvariant";

const CATEGORY_IDS_WITH_INVESTMENT = ["investment", "expenses"];

const resolver = fundFamilyUseAllocationTableResolver(
  zodResolver(capitalCallReallocationCreateBody),
  "fund_family_allocation_table.rows",
);

export const CapitalEventReallocationUpdatePage: React.FC = () => {
  const { fundFamilyId, reallocationFundFamilyEventId: fundFamilyEventId } =
    useRequiredStringParams();
  const queryClient = useQueryClient();
  const { t } = useTranslation(["capitalCall"]);
  const { navigateNext } = usePageWizard();
  const {
    isOpen: isEditDateModalOpen,
    onOpen: openEditDateModal,
    onClose: closeEditDateModal,
  } = useDisclosure();

  const { data: eventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const { data: draftReallocationData } =
    useCapitalCallReallocationDraftDataRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
    );

  const initialTransactionDate =
    draftReallocationData.transaction_date ||
    eventData.posted_date ||
    nowAsIsoDate();

  const [transactionDate, setDate] = useState(initialTransactionDate);

  const { data: allocationTableData } =
    useFundFamilyEventCapitalCallReallocationTableRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
    );

  const {
    mutateAsync: upsertReallocation,
    variables: createVariables,
    error: createError,
    reset: createReset,
  } = useCapitalCallReallocationCreate({
    mutation: {
      onSuccess: async () => {
        await invalidateQueryKeys(
          queryClient,
          getFundFamilyEventCapitalCallReallocationTableRetrieveQueryKey(
            fundFamilyId,
            fundFamilyEventId,
          ),
          getFundFamilyEventRetrieveQueryKey(fundFamilyId, fundFamilyEventId),
        );
      },
    },
  });
  const onSubmit = async (data: {
    fund_family_allocation_table: FundFamilyUseAllocationTableData;
  }) => {
    await upsertReallocation({
      fundFamilyId,
      fundFamilyEventId,
      data: {
        ...data,
        transaction_date: transactionDate,
      },
    });
  };
  const getErrorForCell =
    useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse(
      createError,
      useCallback(
        (rowId) =>
          createVariables?.data.fund_family_allocation_table.rows.findIndex(
            (it) => it.id === rowId,
          ),
        [createVariables],
      ),
      "fund_family_allocation_table.rows",
    );

  const form = useForm<CapitalCallReallocationCreateDataRequest>({
    defaultValues: {
      fund_family_allocation_table: { rows: allocationTableData.rows },
      transaction_date: transactionDate,
    },
    resolver,
  });

  const populateEmptyRow =
    useCallback<PopulateEmptyFundFamilyUseAllocationTableRowFn>((data) => {
      const newRow: FundFamilyUseAllocationTableRow = cloneDeep(data);
      newRow.column_data = {
        ...newRow.column_data,
        type: "capital_call_reallocation",
        investment_id: null,
        transaction_code_id: "",
      } as FundFamilyUseAllocationTableColumnDataCapitalCallReallocation;
      return newRow;
    }, []);
  const { rows, onRowChange, onRowRemove, onRowAdd } =
    useFundFamilyUseAllocationTableFormRowController(
      form,
      "fund_family_allocation_table.rows",
      populateEmptyRow,
    );

  const allocationsByLookupKey = useGetAllocationsByLookupKey(
    fundFamilyId,
    useMemo(
      () =>
        rows.map((row) => {
          invariant(row.column_data?.type === "capital_call_reallocation");
          return {
            transaction_code_id: row.column_data.transaction_code_id,
            investment_id: row.column_data.investment_id || null,
          };
        }),
      [rows],
    ),
    eventData.effective_date,
  );
  const getAllocation =
    useCallback<FundFamilyUseAllocationTableGetAllocationFn>(
      (params) => {
        const columnData = params.row.column_data;
        if (columnData?.type === "capital_call_reallocation") {
          const fundAllocations = allocationsByLookupKey?.({
            transaction_code_id: columnData.transaction_code_id,
            investment_id: columnData.investment_id || null,
          });
          const fundInfo = fundAllocations?.find(
            (it) => it.id === params.fundDef.fund_id,
          );
          if (!fundInfo) {
            return undefined;
          }
          return {
            ownershipPercentage: fundInfo.ownershipPercentage,
            allocationRuleId: fundInfo.allocationRuleId,
            allocationRuleName: fundInfo.allocationRuleName,
          };
        }
        return undefined;
      },
      [allocationsByLookupKey],
    );

  const valueGetter = useCallback<
    ValueGetterFunc<FundFamilyUseAllocationTableRow>
  >((params) => {
    const colId = params.column.getColId();
    if (params.node?.data?.column_data) {
      const columnData = params.node.data
        .column_data as FundFamilyUseAllocationTableColumnDataCapitalCallReallocation;
      if (colId === "final_call_amount_total") {
        const beforeAmount = decimalOrZero(
          columnData.original_call_amount_total,
        );
        const reallocationAmount = decimalOrZero(
          params.node.data.fund_family["capital_call_reallocation"].amount,
        );
        return beforeAmount.add(reallocationAmount).toString();
      } else if (colId.startsWith("final_call_amount_by_fund.")) {
        const fundId = colId.split(".")[1];
        const beforeAmount = decimalOrZero(
          columnData.original_call_amount_by_fund?.[fundId],
        );
        const reallocationAmountForFund = decimalOrZero(
          params.node.data.funds[fundId]["capital_call_reallocation"].amount,
        );
        return beforeAmount.add(reallocationAmountForFund).toString();
      }
    }
    return get(params.data, params.column.getColDef().field!);
  }, []);

  return (
    <PageBody>
      <PageHeader
        title={t("capitalCall:reallocation.reallocateCapitalCall")}
        stats={
          <CapitalCallReallocationStatCard
            reallocationData={eventData}
            openEditDateModal={openEditDateModal}
            transactionDate={transactionDate}
          />
        }
        workflowName={t("capitalCall:reallocation.workflow")}
      />
      <Form
        form={form}
        excludeNonFieldErrors={["fund_family_allocation_table"]}
        onSubmit={onSubmit}
        onBeforeSubmit={createReset}
        onSuccess={navigateNext}
      >
        <TableBox isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("capitalCall:reallocation.inputReallocationAmounts")}
            </TableBoxTitle>
            <TableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <FundFamilyUseAllocationTable
              tableDef={allocationTableData.definition}
              onRowAdd={onRowAdd}
              onRowRemove={onRowRemove}
              onRowChange={onRowChange}
              getAllocation={getAllocation}
              valueGetter={valueGetter}
              tableData={{ rows }}
              getErrorForCell={getErrorForCell}
              columnCellRendererSelector={columnCellRendererSelector}
            />
          </TableBoxBody>
        </TableBox>
        <PageWizardFormFooter submitPermission={PermissionTypeEnum.write} />
      </Form>
      {isEditDateModalOpen && (
        <CapitalCallReallocationEditDateModal
          onCloseComplete={closeEditDateModal}
          effectiveDate={transactionDate}
          setTransactionDate={setDate}
        />
      )}
    </PageBody>
  );
};

const columnCellRendererSelector: FundFamilyUseAllocationTableColumnCellRendererSelector =
  (columnDef, params) => {
    const categoryId = params.data?.category_id;
    if (columnDef.id === "transaction_code_id") {
      return ReallocationTransactionSelectCellRenderer;
    } else if (
      columnDef.id === "investment_id" &&
      categoryId &&
      CATEGORY_IDS_WITH_INVESTMENT.includes(categoryId)
    ) {
      return CapitalCallInvestmentSelectCellRenderer;
    }
  };

export const ReallocationTransactionSelectCellRenderer: React.FC<
  ICellRendererParams<
    FundFamilyUseAllocationTableRow,
    string,
    FundFamilyUseAllocationTableContext
  >
> = (params) => {
  const { t } = useTranslation(["capitalCall"]);

  const { tableDef } = params.context;
  const fundFamilyId = tableDef.fund_family.fund_family_id;

  const { data } = useTransactionCodeConfigListSuspense(fundFamilyId, {
    call_or_distribution: CallOrDistributionEnum.call,
    classification: params.data?.category_id
      ? categoryIdToClassification[params.data.category_id]
      : undefined,
  });

  const ref = useRef<never>(null);

  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <SimpleSelect
        ref={ref}
        title={t("capitalCall:selectTransactionCode")}
        value={params.value ?? undefined}
        onChangeValue={(value) => {
          /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
          params.setValue!(value?.toString() ?? undefined);
        }}
        variant="simple-table"
        isDisabled={params.node.data?.is_read_only || false}
        error={getMaybernTableCellError(params)}
      >
        {data.map(({ transaction_code_id, rich_name }) => (
          <SelectOption value={transaction_code_id} key={transaction_code_id}>
            {rich_name}
          </SelectOption>
        ))}
      </SimpleSelect>
    </MaybernTableFocusCellRendererWrapper>
  );
};
